import React from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  WbSunnyOutlined,
  NavigationOutlined,
  CloudOutlined,
  Menu as MenuIcon
} from '@mui/icons-material';
import { useDrawer } from './context/DrawerContext';

// Hero Section: The most important part of a landing page. It should immediately grab the user's attention and state the value proposition.
const HeroSection = ({ onSignUpClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        py: 12,
        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center',
        borderRadius: 4,
        my: 4,
      }}
    >
      <Container maxWidth="md">
        <Typography
          variant={isMobile ? "h4" : "h2"}
          component="h1"
          fontWeight={700}
          gutterBottom
        >
          Travel Smarter. Get Ahead of the Weather.
        </Typography>
        <Typography variant="h6" component="h2" mb={4}>
          navscout provides real-time, hyper-local weather information for your journey,
          ensuring you're always prepared for what's ahead.
        </Typography>
        <Button
          variant="contained"
          size="large"
          color="secondary"
          onClick={onSignUpClick}
          sx={{
            fontWeight: 'bold',
            borderRadius: 8,
            boxShadow: '0px 8px 15px rgba(0, 0, 0, 0.2)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0px 12px 20px rgba(0, 0, 0, 0.3)',
            },
          }}
        >
          Start Your Free Trial
        </Button>
      </Container>
    </Box>
  );
};

// Features Section: This section highlights the key benefits of the app using a simple card layout.
const FeaturesSection = () => {
  const features = [
    {
      icon: <WbSunnyOutlined color="primary" sx={{ fontSize: 50 }} />,
      title: 'Real-Time Updates',
      description: 'Get the most current weather conditions and forecasts for your exact location, updated moment-to-moment.',
    },
    {
      icon: <NavigationOutlined color="primary" sx={{ fontSize: 50 }} />,
      title: 'Route-Specific Forecasts',
      description: 'Input your travel route and get a detailed forecast for every leg of your journey, not just your destination.',
    },
    {
      icon: <CloudOutlined color="primary" sx={{ fontSize: 50 }} />,
      title: 'Predictive Alerts',
      description: 'Receive proactive notifications for severe weather, allowing you to adjust your travel plans before it’s too late.',
    },
  ];

  return (
    <Box sx={{ py: 8 }}>
      <Container maxWidth="md">
        <Typography variant="h4" component="h3" textAlign="center" mb={6} fontWeight={600}>
          Why Choose navscout?
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 4, transition: 'transform 0.3s' , '&:hover': { transform: 'scale(1.05)'} }}>
                {feature.icon}
                <Typography variant="h6" mt={2} mb={1} fontWeight={600}>
                  {feature.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

// Testimonials Section: Builds trust and credibility by showing positive feedback from users.
const TestimonialsSection = () => {
    const testimonials = [
      {
        text: "navscout saved my road trip! The route-specific alerts helped me avoid a major hailstorm. I won't travel without it now.",
        author: "Alex P.",
      },
      {
        text: "The real-time updates are incredibly accurate. It's the only weather app I trust for my long-haul drives.",
        author: "Maria S.",
      },
      {
        text: "Simple, clean, and exactly what I need. The best app for anyone who spends a lot of time on the road.",
        author: "John L.",
      },
    ];

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
    return (
      <Box sx={{ py: 8, bgcolor: 'grey.100' }}>
        <Container maxWidth="md">
          <Typography variant="h4" component="h3" textAlign="center" mb={6} fontWeight={600}>
            What Our Users Are Saying
          </Typography>
          <Grid container spacing={isMobile ? 2 : 4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 4 }}>
                  <Typography variant="body1" sx={{ fontStyle: 'italic', mb: 2 }}>
                    "{testimonial.text}"
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                    - {testimonial.author}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  };

// Final CTA Section: A concluding section to capture users who have scrolled through the page.
const FinalCTASection = ({ onSignUpClick }) => {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Typography variant="h4" component="h3" mb={2} fontWeight={600}>
            Ready to Start Your Journey?
          </Typography>
          <Typography variant="body1" mb={4}>
            Join thousands of travelers who rely on navscout to stay safe and informed.
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="primary"
            onClick={onSignUpClick}
            sx={{ fontWeight: 'bold', borderRadius: 8 }}
          >
            Sign Up for Free
          </Button>
        </Container>
      </Box>
    );
  };
  
// App-wide Footer
const AppFooter = () => (
    <Box
      component="footer"
      sx={{
        py: 3,
        bgcolor: 'grey.900',
        color: 'white',
        textAlign: 'center',
      }}
    >
      <Container maxWidth="md">
        <Stack direction="row" spacing={3} justifyContent="center">
          <Typography variant="body2" component="a" href="/about" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            About
          </Typography>
          <Typography variant="body2" component="a" href="/privacy" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Privacy
          </Typography>
          <Typography variant="body2" component="a" href="/terms" sx={{ color: 'white', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Terms
          </Typography>
        </Stack>
        <Typography variant="body2" color="white" mt={2}>
          © 2025 navscout. All rights reserved.
        </Typography>
      </Container>
    </Box>
);

// Main App Component
const Home = () => {
const {openDrawer} = useDrawer();
  const handleSignUp = () => {
    openDrawer('sign-up');
  };

const handleSignIn = () => {
    openDrawer('sign-in');
  };

  // The final layout bringing all the sections together.
  return (
    <Box>
      <AppBar position="static" color="transparent" elevation={0} sx={{py: 2}}>
        <Toolbar>
          <Typography
            variant="h5"
            component="div"
            sx={{ flexGrow: 1, fontWeight: 'bold' }}
            color="primary"
          >
            navscout
          </Typography>
          <Button color="primary" onClick={handleSignIn} variant="outlined" sx={{ fontWeight: 'bold' }}>
            Sign In
          </Button>
        </Toolbar>
      </AppBar>

      <HeroSection onSignUpClick={handleSignUp} />
      <FeaturesSection />
      {/* <TestimonialsSection /> */}
      <FinalCTASection onSignUpClick={handleSignUp} />
      <AppFooter />
    </Box>
  );
};

export default Home;
